import React, { useEffect, useMemo, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import api from '../sever/Api';

const TestsManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pageCount: 0, hasNext: false, hasPrev: false, nextPage: null, prevPage: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [testTypeFilter, setTestTypeFilter] = useState('all');

  const getTestTypeLabel = (type) => {
    const map = {
      competency: 'Competency',
      bigfive: 'Big Five',
      viacharacter: 'VIA Character',
      high5: 'High5',
      emotionalintelligence: 'Emotional Intelligence',
      disc: 'DiSC',
      ikigai: 'Ikigai',
      johari: 'Johari',
      mbti: 'MBTI',
      learningstyle: 'Learning Style',
      enneagram: 'Enneagram',
      sevenquestion: 'Essence Echo',
    };
    return map[String(type || '').toLowerCase()] || type || '-';
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  const fetchTests = async (targetPage = page) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: targetPage, limit };
      if (testTypeFilter !== 'all') params.testType = testTypeFilter;
      const res = await api.get('/api/admin/tests', { params });
      const list = Array.isArray(res.data?.tests) ? res.data.tests.slice() : [];
      // Exclude tests where user is missing/deleted OR lacks name/email
      const filtered = list.filter((it) => {
        const user = it?.user;
        if (!user) return false;
        if (user.deleted === true || user.isDeleted === true) return false;
        const hasEmail = Boolean(String(user.email || '').trim());
        const hasName = Boolean(
          String(user.firstName || '').trim() || String(user.lastName || '').trim()
        );
        return hasEmail && hasName;
      });
      filtered.sort((a, b) => new Date(b.lastTakenAt || 0) - new Date(a.lastTakenAt || 0));
      setItems(filtered);
      if (res.data?.pagination) {
        setPagination(res.data.pagination);
        setPage(res.data.pagination.page || targetPage);
      } else {
        setPagination({
          total: (res.data?.tests || []).length,
          page: targetPage,
          limit,
          pageCount: 1,
          hasNext: false,
          hasPrev: targetPage > 1,
          nextPage: null,
          prevPage: targetPage > 1 ? targetPage - 1 : null,
        });
      }
    } catch (e) {
      setError('Failed to load tests');
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchTests(1);
  }, [testTypeFilter]);

  useEffect(() => {
    fetchTests();
  }, []);

  const formatDate = (d) => {
    try { return new Date(d).toLocaleString(); } catch { return '-'; }
  };

  return (
    <div className='flex flex-col lg:flex-row min-h-screen w-full overflow-x-hidden'>
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
      </div>
      <div className='ml-64 flex-1 relative'>
        <Navbar toggleSidebar={toggleSidebar} />
        <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB]">
          <div className="w-full max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between">
              <h1 className="text-2xl font-bold text-gray-800">Tests Management</h1>
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600">Test</label>
                <select value={testTypeFilter} onChange={(e) => setTestTypeFilter(e.target.value)} className="px-2 py-1 border rounded text-sm">
                  <option value="all">All</option>
                  <option value="competency">Competency</option>
                  <option value="bigfive">Big Five</option>
                  <option value="viacharacter">VIA Character</option>
                  <option value="high5">High5</option>
                  <option value="emotionalintelligence">Emotional Intelligence</option>
                  <option value="disc">DiSC</option>
                  <option value="ikigai">Ikigai</option>
                  <option value="johari">Johari</option>
                  <option value="mbti">MBTI</option>
                  <option value="learningstyle">Learning Style</option>
                  <option value="enneagram">Enneagram</option>
                  <option value="sevenquestion">Essence Echo</option>
                </select>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div className="text-red-600 text-sm">{error}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border">
                    <thead>
                      <tr className="bg-gray-50 text-left text-sm">
                        <th className="px-3 py-2 border">User Name</th>
                        <th className="px-3 py-2 border">User Email</th>
                        <th className="px-3 py-2 border">Test Name</th>
                        <th className="px-3 py-2 border">Last Taken</th>
                        <th className="px-3 py-2 border">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={`${it.userId}-${it.testType}`} className="text-sm">
                          <td className="px-3 py-2 border">{[it?.user?.firstName, it?.user?.lastName].filter(Boolean).join(' ') || '-'}</td>
                          <td className="px-3 py-2 border">{it?.user?.email || '-'}</td>
                          <td className="px-3 py-2 border">{getTestTypeLabel(it.testType)}</td>
                          <td className="px-3 py-2 border">{formatDate(it.lastTakenAt)}</td>
                          <td className="px-3 py-2 border">
                            <a
                              style={{ background: 'rgb(179, 157, 219)' }}
                              className="px-3 py-1 text-white bg-blue-600 rounded inline-block"
                              href={`/admin/tests/${it.testResultId}`}
                            >
                              View Report
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-600">
                      Showing page {pagination.page} of {Math.max(pagination.pageCount || 0, 1)}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        className={`px-3 py-1 border rounded ${pagination.hasPrev ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        disabled={!pagination.hasPrev || loading}
                        onClick={() => fetchTests(page - 1)}
                      >
                        Prev
                      </button>

                      {Array.from({ length: pagination.pageCount }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          className={`px-3 py-1 border rounded ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white hover:bg-gray-50'}`}
                          disabled={loading}
                          onClick={() => fetchTests(p)}
                        >
                          {p}
                        </button>
                      ))}

                      <button
                        className={`px-3 py-1 border rounded ${pagination.hasNext ? 'bg-white hover:bg-gray-50' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                        disabled={!pagination.hasNext || loading}
                        onClick={() => fetchTests(page + 1)}
                      >
                        Next
                      </button>
                    </div>

                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestsManagement;


